import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useEmpresas } from '@/api/queries/empresas';
import { useContatosGlobal } from '@/api/queries/contatos';
import {
  Building2,
  Users,
  Inbox,
  KanbanSquare,
  Activity,
  Package,
  FileText,
  LayoutDashboard,
  Search,
  Plus,
  ArrowRight,
} from 'lucide-react';
import { cn } from '@/lib/cn';

type CommandKind = 'empresa' | 'contato' | 'go' | 'action';

interface CommandItem {
  id: string;
  kind: CommandKind;
  label: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  onSelect: () => void;
}

const GO_ITEMS: { label: string; to: string; icon: typeof Building2 }[] = [
  { label: 'Visão geral', to: '/dashboard', icon: LayoutDashboard },
  { label: 'Inbox', to: '/inbox', icon: Inbox },
  { label: 'Pipeline', to: '/pipeline', icon: KanbanSquare },
  { label: 'Empresas', to: '/empresas', icon: Building2 },
  { label: 'Contatos', to: '/contatos', icon: Users },
  { label: 'Eventos', to: '/eventos', icon: Activity },
  { label: 'Serviços', to: '/servicos', icon: Package },
  { label: 'Templates', to: '/templates', icon: FileText },
];

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenNewEmpresa: () => void;
}

export function CommandPalette({ open, onOpenChange, onOpenNewEmpresa }: CommandPaletteProps) {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const enabledQuery = open && q.trim().length >= 2;
  const { data: empresas } = useEmpresas(
    enabledQuery ? { q: q.trim(), limit: 8 } : { limit: 0 },
  );
  const { data: contatos } = useContatosGlobal(
    enabledQuery ? { q: q.trim(), limit: 5 } : { limit: 0 },
  );

  useEffect(() => {
    if (!open) {
      setQ('');
      setHighlight(0);
    } else {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const items: CommandItem[] = useMemo(() => {
    const all: CommandItem[] = [];

    if (enabledQuery && empresas && empresas.length > 0) {
      empresas.slice(0, 8).forEach((e) => {
        all.push({
          id: `empresa:${e.id}`,
          kind: 'empresa',
          label: e.nome,
          hint: [e.setor, e.cidade].filter(Boolean).join(' · '),
          icon: Building2,
          onSelect: () => {
            nav(`/empresas/${e.id}`);
            onOpenChange(false);
          },
        });
      });
    }
    if (enabledQuery && contatos && contatos.length > 0) {
      contatos.slice(0, 5).forEach((c) => {
        all.push({
          id: `contato:${c.id}`,
          kind: 'contato',
          label: c.nome,
          hint: `${c.cargo_titulo_livre ?? c.papel} · ${c.empresa_nome}`,
          icon: Users,
          onSelect: () => {
            nav(`/empresas/${c.empresa_id}`);
            onOpenChange(false);
          },
        });
      });
    }

    const ql = q.trim().toLowerCase();
    GO_ITEMS.forEach((g) => {
      if (!ql || g.label.toLowerCase().includes(ql)) {
        all.push({
          id: `go:${g.to}`,
          kind: 'go',
          label: g.label,
          hint: g.to,
          icon: g.icon,
          onSelect: () => {
            nav(g.to);
            onOpenChange(false);
          },
        });
      }
    });

    if (!ql || 'nova empresa'.includes(ql) || 'adicionar empresa'.includes(ql)) {
      all.push({
        id: 'action:new-empresa',
        kind: 'action',
        label: 'Nova empresa',
        hint: 'criar do zero · enriquecer via URL/CNPJ',
        icon: Plus,
        onSelect: () => {
          onOpenChange(false);
          setTimeout(onOpenNewEmpresa, 50);
        },
      });
    }

    return all;
  }, [enabledQuery, empresas, contatos, q, nav, onOpenChange, onOpenNewEmpresa]);

  useEffect(() => {
    setHighlight(0);
  }, [q]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onOpenChange(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(h - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const it = items[highlight];
        if (it) it.onSelect();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, items, highlight, onOpenChange]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-cmd-index="${highlight}"]`);
    if (el && 'scrollIntoView' in el) {
      (el as HTMLElement).scrollIntoView({ block: 'nearest' });
    }
  }, [highlight]);

  if (!open) return null;

  let groupHeader = '';

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] bg-foreground/30 backdrop-blur-[2px]"
        onClick={() => onOpenChange(false)}
      />
      <div className="fixed left-1/2 top-[12vh] z-[101] w-[640px] max-w-[92vw] -translate-x-1/2">
        <div className="rounded-xl border border-border/70 bg-popover shadow-2xl shadow-foreground/10">
          <div className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground/70" strokeWidth={2.25} />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="buscar empresa, contato, ou ação..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/60"
            />
            <kbd className="hidden sm:inline-flex mono items-center rounded border border-border/70 px-1.5 py-0.5 text-[10px] text-muted-foreground">
              esc
            </kbd>
          </div>

          <div ref={listRef} className="max-h-[60vh] overflow-y-auto p-1">
            {items.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                {enabledQuery ? 'Sem resultados.' : 'Digite pra buscar empresas e contatos…'}
              </p>
            ) : (
              items.map((it, idx) => {
                const kindLabel = { empresa: 'empresa', contato: 'contato', go: 'ir', action: 'ação' }[it.kind];
                const showHeader = idx === 0 || items[idx - 1]!.kind !== it.kind;
                const Icon = it.icon;
                if (showHeader) groupHeader = kindLabel;
                return (
                  <div key={it.id}>
                    {showHeader && (
                      <div className="mt-1 px-3 pb-1 pt-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                        {groupHeader}
                      </div>
                    )}
                    <button
                      type="button"
                      data-cmd-index={idx}
                      onClick={it.onSelect}
                      onMouseEnter={() => setHighlight(idx)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors',
                        highlight === idx ? 'bg-muted' : 'hover:bg-muted/60',
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2.25} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium">{it.label}</p>
                        {it.hint && (
                          <p className="truncate text-[11px] text-muted-foreground lowercase">
                            {it.hint}
                          </p>
                        )}
                      </div>
                      {highlight === idx && (
                        <ArrowRight className="h-3 w-3 text-muted-foreground" strokeWidth={2.25} />
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between border-t border-border/70 px-3 py-1.5 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <kbd className="mono rounded border border-border/70 px-1">↑↓</kbd> navegar
              <kbd className="mono rounded border border-border/70 px-1">↵</kbd> abrir
            </span>
            <span>
              <kbd className="mono rounded border border-border/70 px-1">⌘K</kbd> abrir command palette
            </span>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
